import React from 'react';

import { cn } from 'domain/core';
import { HeadingProps, SpanProps } from './common-types';

export const Text = React.forwardRef((props: SpanProps, ref: any) => {
	const { className, ...rest } = props;

	return (
		<span
			className={cn('text', 'text-ellipsis text-neutral-600', className)}
			{...rest}
			ref={ref}
		/>
	);
});

export const Title = React.forwardRef((props: HeadingProps, ref: any) => {
	const { className, ...rest } = props;

	return (
		<h1
			className={cn(
				'title',
				'pt-2 pb-2',
				'text-2xl',
				'text-primary-900',
				className
			)}
			ref={ref}
			{...rest}
		/>
	);
});

export type EasingFunction = (
	time: number,
	initialValue: number,
	endValue: number,
	duration: number
) => number;

const easeOutCubic: EasingFunction = (
	t: number,
	b: number,
	c: number,
	d: number
) => c * ((t = t / d - 1) * t * t + 1) + b;

interface AnimatedNumberProps extends Omit<SpanProps, 'children'> {
	from?: number;
	to: number;
	autoStart?: boolean;
	duration?: number;
	easing?: EasingFunction;
}
export const AnimatedNumber = ({
	from = 0,
	to,
	autoStart = true,
	duration = 3000,
	easing = easeOutCubic,
	className,
	ref,
	...props
}: AnimatedNumberProps) => {
	const spanRef = React.useRef<HTMLSpanElement>(null);

	const animate = () => {
		if (spanRef.current) {
			let startTime: number;
			let frame: number;

			const run: FrameRequestCallback = (timestamp) => {
				if (!startTime) {
					startTime = timestamp;
				}
				const currTime = timestamp - startTime;
				const newVal = easing(currTime, from, to, duration);
				if (spanRef.current) {
					spanRef.current.innerText = newVal.toFixed();
				}
				window.cancelAnimationFrame(frame);
				if (currTime < duration) {
					frame = window.requestAnimationFrame(run);
				}
			};

			frame = window.requestAnimationFrame(run);
		}
	};

	React.useEffect(animate, []);

	return <Text className={cn('', className)} ref={spanRef} {...props} />;
};
